import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import { AreaOfUse } from '../../../../core/model/area-of-use.model';
import { PyStateUtils } from '../../../../core/state/utils';
import {
  AREA_OF_USE_ADMIN_FEATURE,
  AreaOfUseAdminListState,
  AreaOfUseAdminState,
  StateWithAreaOfUseAdmin,
} from '../area-of-use-admin-state';

export const getAreaOfUseAdminState: MemoizedSelector<StateWithAreaOfUseAdmin, AreaOfUseAdminState> =
  createFeatureSelector<AreaOfUseAdminState>(AREA_OF_USE_ADMIN_FEATURE);

// LIST
export const getAreaOfUseAdminListState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<AreaOfUseAdminListState>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.list);

export const getAreaOfUseAdminListLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAreaOfUseAdminListState,
  (state) => (state && StateUtils.loaderLoadingSelector(state)) || false
);

export const getAreaOfUseAdminListSuccess: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAreaOfUseAdminListState,
  (state) => (state && StateUtils.loaderSuccessSelector(state)) || false
);

export const getAreaOfUseAdminListCodes: MemoizedSelector<StateWithAreaOfUseAdmin, string[] | undefined> = createSelector(
  getAreaOfUseAdminState,
  (state: AreaOfUseAdminState) => state.list && StateUtils.loaderValueSelector(state.list)?.results
);

// CATEGORIES
export const getAreaOfUseAdminCategoriesState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.EntityLoaderState<AreaOfUse>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.categories);

export const getAreaOfUseAdminCategoriesEntities: MemoizedSelector<StateWithAreaOfUseAdmin, StateUtils.LoaderState<AreaOfUse>[]> =
  createSelector(
    getAreaOfUseAdminListCodes,
    getAreaOfUseAdminCategoriesState,
    (refs: string[], categories: StateUtils.EntityLoaderState<AreaOfUse>) =>
      (refs && categories && refs.map((ref) => categories.entities[ref])) || undefined
  );

export const getAreaOfUseAdminCategories: MemoizedSelector<StateWithAreaOfUseAdmin, AreaOfUse[]> = createSelector(
  getAreaOfUseAdminCategoriesEntities,
  (entities: StateUtils.LoaderState<AreaOfUse>[]) =>
    (entities && entities.map((entity) => StateUtils.loaderValueSelector(entity))) || undefined
);

export const getAreaOfUseAdminCategoriesEntitiesByCodes = (
  codes: string[]
): MemoizedSelector<StateWithAreaOfUseAdmin, StateUtils.LoaderState<AreaOfUse>[]> =>
  createSelector(
    getAreaOfUseAdminCategoriesState,
    (state: StateUtils.EntityLoaderState<AreaOfUse>) =>
      codes && codes.map((code) => PyStateUtils.entityLoaderStateSelector(state, code))
  );

export const getAreaOfUseAdminCategoriesByCodes = (codes: string[]): MemoizedSelector<StateWithAreaOfUseAdmin, AreaOfUse[]> => {
  return createSelector(
    getAreaOfUseAdminCategoriesEntitiesByCodes(codes),
    (areaOfUseCategoriesEntities) =>
      areaOfUseCategoriesEntities && areaOfUseCategoriesEntities.map((entity) => StateUtils.loaderValueSelector(entity))
  );
};

export const getAreaOfUseAdminCategoryEntityByCode = (
  code: string
): MemoizedSelector<StateWithAreaOfUseAdmin, StateUtils.LoaderState<AreaOfUse>> =>
  createSelector(
    getAreaOfUseAdminCategoriesState,
    (state: StateUtils.EntityLoaderState<AreaOfUse>) => state && PyStateUtils.entityLoaderStateSelector(state, code)
  );

export const getAreaOfUseCategoryByCode = (code: string): MemoizedSelector<StateWithAreaOfUseAdmin, AreaOfUse> => {
  return createSelector(
    getAreaOfUseAdminCategoryEntityByCode(code),
    (areaOfUseCategoryEntity) => areaOfUseCategoryEntity && StateUtils.loaderValueSelector(areaOfUseCategoryEntity)
  );
};

// For provided Area of Use category codes, returns array of codes of categories that are currently in loading state. Used to display spinners after editing category.
export const getAreaOfUseCategoriesLoadingCodesArray = (codes: string[]): MemoizedSelector<StateWithAreaOfUseAdmin, string[]> => {
  return createSelector(getAreaOfUseAdminCategoriesEntitiesByCodes(codes), (areaOfUseCategoriesEntities) =>
    areaOfUseCategoriesEntities
      .filter((areaOfUseCategoryEntity) => StateUtils.loaderLoadingSelector(areaOfUseCategoryEntity) === true)
      .map((areaOfUseCategoryEntity) => StateUtils.loaderValueSelector(areaOfUseCategoryEntity)?.code)
  );
};

export const getLoadAreasOfUseForProductLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAreaOfUseAdminState,
  (state: AreaOfUseAdminState) => StateUtils.loaderLoadingSelector(state.products.list)
);

export const getAddProductToAreaOfUseState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<AreaOfUse>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.products.add);

export const getAddProductToAreaOfUseSuccess: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAddProductToAreaOfUseState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderSuccessSelector(state)
);

export const getAddProductToAreaOfUseLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAddProductToAreaOfUseState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderLoadingSelector(state)
);

export const getRemoveProductsFromAreaOfUseState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<AreaOfUse>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.products.remove);

export const getRemoveProductsFromAreaOfUseSuccess: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getRemoveProductsFromAreaOfUseState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderSuccessSelector(state)
);

export const getRemoveProductsFromAreaOfUseLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getRemoveProductsFromAreaOfUseState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderLoadingSelector(state)
);

// PUBLISH
export const getAreaOfUseAdminPublishState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<string>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.publish);

export const getAreaOfUseAdminPublishLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getAreaOfUseAdminPublishState,
  (state) => (state && StateUtils.loaderLoadingSelector(state)) || false
);

export const getLoadAreaOfUseCategoryDetailsState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<AreaOfUse>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.details);

export const getLoadAreaOfUseCategoryDetailsLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getLoadAreaOfUseCategoryDetailsState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderLoadingSelector(state)
);

export const getReplaceProductFromAreaOfUseState: MemoizedSelector<
  StateWithAreaOfUseAdmin,
  StateUtils.LoaderState<AreaOfUse>
> = createSelector(getAreaOfUseAdminState, (state: AreaOfUseAdminState) => state.products.replace);

export const getReplaceAreaOfUseProductLoading: MemoizedSelector<StateWithAreaOfUseAdmin, boolean> = createSelector(
  getReplaceProductFromAreaOfUseState,
  (state: StateUtils.LoaderState<AreaOfUse>) => StateUtils.loaderLoadingSelector(state)
);
